import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import TextAreaField from 'components/TextAreaField';
import InputField from 'components/InputField';
import { Button } from 'ncoded-component-library';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { DataRoom } from 'router/subrouters/DataRoom/models/DataRoom';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import validations from 'validations';
import TeamsField from 'components/TeamsField';
import { MAX_NUM_OF_TEAMS } from 'components/TeamsField/TeamsField.component';
import WhitelabelingBanner from 'router/subrouters/DataRoom/components/WhitelabelingBanner';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import ManageKeyFeatures from './components/ManageKeyFeatures';
import ManageQuestionSubjectsDrawer from 'router/subrouters/DataRoom/components/ManageQuestionSubjectsDrawer';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import useCallbackRef from 'hooks/useCallbackRef';
import SettingsIcon from 'icons/Settings.icon';
import Snapshots from './components/Snapshots';
import CheckboxField from 'components/CheckboxFieldGroup/CheckboxField';
import DocumentsGlobalSecurity from './components/DocumentsGlobalSecurity';
import { MOBILE_SETTINGS_SELECTOR } from 'router/subrouters/DataRoom/providers/Onboarding/data';
import ResolvePopup from 'components/ResolvePopup';
import InformationcircleIcon from 'icons/Informationcircle.icon';
import CheckToggleField from 'components/CheckToggleField';
import DurationDaysField from 'components/DurationDaysField';
import { Navigate } from 'react-router-dom';
import { getDataRoomRoute } from 'utils';

import './SetupPage.styles.scss';
import './SetupPage.styles.responsive.scss';

type SetupPageProps = {
  className?: string;
};

const MIN_SESSION_MINUTES = 10;
const MAX_SESSION_MINUTES = 1000;
const INITIAL_FILE_ACCESS_DAYS = 14;
const INITIAL_EXTEND_ACCESS_DAYS = 5;
const MIN_DAYS_FOR_FILE_ACCESS = 4;

const SetupPage: React.FC<SetupPageProps> = (props) => {
  const { className } = props;

  const { popServerError, popSuccess } = useContext(PopNotificationsContext);
  const { isAdmin, doesntHaveAccess, dataRoom, setDataRoom } =
    useContext(DataRoomContext);

  const { _id, isWhitelabeled } = dataRoom;

  const [manageQuestionSubjectDrawer, manageQuestionSubjectDrawerRef] =
    useCallbackRef<SideDrawerRef | null>(null);

  const classes = classNames('setup-page', 'page', className);

  const initialValues = useMemo(() => {
    const {
      teams,
      customWelcomeMessage,
      sessionTimeout,
      accessConfirmation = true,
      accessConfirmationDays = INITIAL_FILE_ACCESS_DAYS,
      buySideCanInviteColleagues = false,
      canRenewFileAccess = false,
      renewFileAccessDays = INITIAL_EXTEND_ACCESS_DAYS,
      allowSoftSignals = false,
    } = dataRoom;

    return {
      customWelcomeMessage,
      sessionTimeout,
      buySideCanInviteColleagues,
      accessConfirmation,
      accessConfirmationDays,
      canRenewFileAccess,
      renewFileAccessDays,
      allowSoftSignals,
      teams: teams || [],
    };
  }, [dataRoom]);

  const { t } = useTranslation();

  const handleSubmit = useCallback(
    async (values: DataRoom) => {
      if (doesntHaveAccess) return;

      const {
        accessConfirmation,
        accessConfirmationDays,
        canRenewFileAccess,
        renewFileAccessDays,
        ...rest
      } = values;

      const newValues = {
        ...rest,
        accessConfirmation,
        canRenewFileAccess,
        ...(accessConfirmation &&
          accessConfirmationDays && {
            accessConfirmationDays: +accessConfirmationDays,
          }),
        ...(canRenewFileAccess &&
          renewFileAccessDays && {
            renewFileAccessDays: +renewFileAccessDays,
          }),
      };

      try {
        const { data } = await dataRoomApi.room.patchDataRoom(_id, newValues);
        setDataRoom((prev) => ({ ...prev, ...data }));
        popSuccess(t('succesfullyUpdatedDataRoom'));
      } catch (e) {
        popServerError(e);
      }
    },
    [_id, doesntHaveAccess, popSuccess, popServerError, setDataRoom, t],
  );

  const validate = useCallback(
    ({
      accessConfirmation,
      accessConfirmationDays,
      canRenewFileAccess,
      renewFileAccessDays,
    }: DataRoom) => {
      const errors = {} as Record<keyof DataRoom, string>;

      if (accessConfirmation && !accessConfirmationDays)
        errors.accessConfirmationDays = t('requiredField');

      if (canRenewFileAccess && !renewFileAccessDays)
        errors.renewFileAccessDays = t('requiredField');

      return errors;
    },
    [t],
  );

  if (!isAdmin)
    return <Navigate to={getDataRoomRoute(_id, 'documents')} replace />;

  return (
    <div className={classes}>
      <div className="setup-page__heading">
        <h2 id={MOBILE_SETTINGS_SELECTOR}>{t('setUpDataRoom')}</h2>
        {!isWhitelabeled && <WhitelabelingBanner />}
      </div>

      <div className="setup-page__content">
        <Form
          onSubmit={handleSubmit}
          validate={validate}
          initialValues={initialValues}
          render={({
            pristine,
            submitting,
            values: { accessConfirmation, canRenewFileAccess },
            handleSubmit,
          }) => (
            <form
              className="setup-page__form white-panel"
              onSubmit={handleSubmit}
            >
              <div className="row">
                <Field
                  name="teams"
                  component={TeamsField}
                  label={t('teamsTitle', { count: MAX_NUM_OF_TEAMS })}
                  description={t('teamsHint')}
                  disabled={doesntHaveAccess}
                />
                <Field
                  label={t('customEmailMessage')}
                  name="customWelcomeMessage"
                  component={TextAreaField}
                  disabled={doesntHaveAccess}
                />
              </div>

              <div className="row">
                <Field
                  name="sessionTimeout"
                  component={InputField}
                  disabled={doesntHaveAccess}
                  label={t('sessionTimeoutMinutes')}
                  validate={validations.composeValidators(
                    validations.minNumber(
                      t('mustBeGreaterThan', { count: MIN_SESSION_MINUTES }),
                      MIN_SESSION_MINUTES,
                    ),
                    validations.maxNumber(
                      t('mustBeLowerThan', { count: MAX_SESSION_MINUTES }),
                      MAX_SESSION_MINUTES,
                    ),
                  )}
                  required
                  description={t('theMinDuration', {
                    count: MIN_SESSION_MINUTES,
                  })}
                />

                <div className="buy-side-permissions">
                  <p>{t('buySidePermissions')}</p>
                  <Field
                    name="buySideCanInviteColleagues"
                    component={CheckboxField}
                    type="checkbox"
                    label={t('buySideInviteColleagues')}
                    disabled={doesntHaveAccess}
                  />
                </div>
              </div>

              <div className="access-confirmation">
                <div className="access-confirmation__title">
                  {t('accessConfirmationSettings')}
                  <ResolvePopup
                    onHover
                    popupContent={t('accessConfirmationSettingsPopup')}
                  >
                    <InformationcircleIcon />
                  </ResolvePopup>
                </div>

                <div>
                  <Field
                    name="accessConfirmation"
                    type="checkbox"
                    component={CheckToggleField}
                    disabled={doesntHaveAccess}
                  />

                  <DurationDaysField
                    name="accessConfirmationDays"
                    label={t('requireBuyersReConfirm')}
                    disabled={!accessConfirmation}
                    minDays={MIN_DAYS_FOR_FILE_ACCESS}
                    copyForMinValidation="minimumConfirmationCycleInDays"
                    copyForMaxValidation="maximumConfirmationCycleInDays"
                  />
                </div>

                {accessConfirmation && (
                  <div className="file-access-extension">
                    <Field
                      name="canRenewFileAccess"
                      type="checkbox"
                      component={CheckToggleField}
                      disabled={doesntHaveAccess}
                    />

                    <DurationDaysField
                      disabled={!canRenewFileAccess}
                      name="renewFileAccessDays"
                      label={t('allowBuyersToExtendFileAccess')}
                      copyForMinValidation="minimumExtentionCycleInDays"
                      copyForMaxValidation="maximumExtentionCycleInDays"
                    >
                      {t('onTheirOwn')}
                    </DurationDaysField>
                  </div>
                )}
              </div>

              <div className="soft-signals-section">
                <p className="soft-signals-section__title">
                  {t('softSignals')}
                </p>

                <Field
                  name="allowSoftSignals"
                  component={CheckToggleField}
                  type="checkbox"
                  label={t('allowSoftSignalsForAllFiles')}
                  disabled={doesntHaveAccess}
                />
              </div>

              {!doesntHaveAccess && (
                <div className="row">
                  <div className="manage-q-a">
                    <p>{t('qaTopics')}</p>
                    <Button
                      variant="link"
                      icon={<SettingsIcon strokeWidth={2} />}
                      onClick={() => manageQuestionSubjectDrawer?.setOpen(true)}
                    >
                      {t('editQATopics')}
                    </Button>
                  </div>
                </div>
              )}

              <div className="actions">
                <Button
                  type="submit"
                  disabled={doesntHaveAccess || pristine || submitting}
                >
                  {t('saveDataRoomSettings')}
                </Button>
              </div>
            </form>
          )}
        />
      </div>

      <DocumentsGlobalSecurity />

      <Snapshots />
      <ManageKeyFeatures />

      <ManageQuestionSubjectsDrawer ref={manageQuestionSubjectDrawerRef} />
    </div>
  );
};

export default SetupPage;
